import { isWithin } from "./columnFilter";
import { fmtDate, fmtDateTime, LOCALE } from "./format";

/**
 * "Revised Confirmations" — a job that is introduction-complete or confirmed
 * and then changed: its move, packing or loading date, or a price / cubic feet
 * move of 25% or more from the original estimate. The backend decides and sends
 * the reasons as `revisions`, keeping the job `confirmed-update` for 7 days;
 * the column filter narrows that to the last day by default. The MC clears a
 * job by reviewing the change with the client (revisionReviewedAt).
 */

const lastRevisedAt = (job) => {
  const times = (job.revisions || []).map((r) => new Date(r.changedAt).getTime());
  return times.length ? new Date(Math.max(...times)).toISOString() : "";
};

/** `days` is the filter window; null means no limit. */
export const isRevisedWithin = (job, days, now = new Date()) => {
  const at = lastRevisedAt(job);
  return at !== "" && isWithin(at, days, now);
};

export const revisedConfirmations = (state, days, now = new Date()) =>
  (state.jobs || []).filter(
    (j) => j.status === "confirmed-update" && isRevisedWithin(j, days, now),
  );

const money = (v) => "$" + Number(v).toLocaleString(LOCALE);

const FIELDS = {
  moveDate: ["Move date", fmtDate],
  packingDate: ["Packing date", fmtDate],
  fullPackingDate: ["Full packing date", fmtDate],
  partialPackingDate: ["Partial packing date", fmtDate],
  unpackingDate: ["Unpacking date", fmtDate],
  localDeliveryDate: ["Local delivery date", fmtDate],
  loadingDate: ["Loading date", fmtDate],
  price: ["Price", money],
  moveFrom: ["Pickup address", String],
  moveTo: ["Delivery address", String],
  totalCubicFeet: ["Move size", (v) => v + " cu ft"],
};

const empty = (v) => v === "" || v === null || v === undefined;

/** One line a coordinator can act on: "Full packing date added: Oct 2, 2026". */
export const describeRevision = (revision) => {
  const [label, format] = FIELDS[revision.field] || [revision.field, String];
  const pct = revision.changePct;
  const moved =
    typeof pct === "number"
      ? ` (${pct > 0 ? "+" : ""}${pct}% vs original estimate)`
      : "";
  if (empty(revision.old) && !empty(revision.new)) {
    return `${label} added: ${format(revision.new)}${moved}`;
  }
  if (!empty(revision.old) && empty(revision.new)) {
    return `${label} removed (was ${format(revision.old)})`;
  }
  return `${label} changed: ${format(revision.old)} → ${format(revision.new)}${moved}`;
};

/**
 * "Changed by Edi Suka, Oct 1, 9:15 AM" — who saved the lead in Zoho and when;
 * "Changed Oct 1, 9:15 AM" when the name is unknown, "" when the time is too.
 */
export const revisionWhen = (revision) => {
  if (!revision.changedAt) return "";
  const by = String(revision.changedBy || "").trim();
  return `Changed ${by ? "by " + by + ", " : ""}${fmtDateTime(revision.changedAt)}`;
};
